import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const StoreContext = createContext(null);

// Internal store slots are fixed ("orewa" = slot 1, "eastridge" = slot 2); each business names its own stores.
const SLOT_FALLBACK = { orewa: 'Store 1', eastridge: 'Store 2' };

export const StoreProvider = ({ children }) => {
  const { token } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(
    localStorage.getItem('selectedStore') || 'orewa'
  );
  const [loading, setLoading] = useState(true);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
  
  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);
  
  const fetchStores = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/stores`, { headers: getAuthHeader() });
      const list = response.data || [];
      setStores(list);
      if (list.length && !list.some(s => s.code === selectedStore)) {
        setSelectedStore(list[0].code);
        localStorage.setItem('selectedStore', list[0].code);
      }
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    } finally {
      setLoading(false);
    }
  }, [token, API_URL, getAuthHeader, selectedStore]);
  
  useEffect(() => {
    fetchStores();
  }, [fetchStores]);
  
  const changeStore = (storeCode) => {
    setSelectedStore(storeCode);
    localStorage.setItem('selectedStore', storeCode);
  };
  
  const getCurrentStore = () => {
    return stores.find(s => s.code === selectedStore) || stores[0] || { code: 'orewa', name: SLOT_FALLBACK.orewa };
  };

  const storeName = useCallback((code) => {
    const key = (code || '').toLowerCase();
    const found = stores.find(s => s.code === key);
    return found?.name || SLOT_FALLBACK[key] || code || '';
  }, [stores]);

  const isMultiStore = stores.length > 1;
  
  return (
    <StoreContext.Provider value={{
      stores,
      selectedStore,
      changeStore,
      getCurrentStore,
      storeName,
      isMultiStore,
      loading,
      refreshStores: fetchStores
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};
